import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Header, ErrorBoundary, LoadingSpinner, Footer } from './components/common';

// Lazy load page components for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const AgentsPage = lazy(() => import('./pages/AgentsPage'));
const WeaponsPage = lazy(() => import('./pages/WeaponsPage'));
const MapsPage = lazy(() => import('./pages/MapsPage'));

// Footer pages
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Stale-while-revalidate strategy - optimized for performance
      staleTime: 1000 * 60 * 60 * 2, // 2 hours - data is fresh for 2 hours
      gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache for 24 hours
      refetchOnMount: false, // Don't refetch on component mount if data is fresh
      refetchOnReconnect: true, // Refetch when reconnecting
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <div className="app">
            <Header />
            <main className="main-content">
              <Suspense
                fallback={
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <LoadingSpinner />
                  </div>
                }
              >
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/agents" element={<AgentsPage />} />
                  <Route path="/weapons" element={<WeaponsPage />} />
                  <Route path="/maps" element={<MapsPage />} />

                  {/* Footer pages */}
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/guide" element={<GuidePage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/faq" element={<FAQPage />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

// 404 Not Found component
const NotFound = () => (
  <div style={{
    textAlign: 'center',
    padding: '4rem 2rem',
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  }}>
    <h1 style={{ fontSize: '4rem', color: 'var(--color-primary)', marginBottom: '1rem' }}>404</h1>
    <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>Page Not Found</h2>
    <p style={{ color: 'var(--color-text-muted)', marginBottom: '2rem' }}>
      The page you're looking for doesn't exist.
    </p>
    <a
      href="/"
      style={{
        color: 'var(--color-primary)',
        textDecoration: 'none',
        padding: '0.75rem 1.5rem',
        border: '2px solid var(--color-primary)',
        borderRadius: '4px',
        transition: 'all 0.3s ease'
      }}
    >
      Go Home
    </a>
  </div>
);

export default App;
